import { BlockProps } from '@/components/Block/Block';
import Dropdown, {
    DropdownAlignment,
} from '@/components/Redactie/Dropdown/Dropdown';
import DropdownButton from '@/components/Redactie/Dropdown/DropdownButton';
import P from '@/components/Redactie/P/P';
import {
    mdiAlertOctagon,
    mdiBookmark,
    mdiCheckboxMarkedCircle,
    mdiHelpCircle,
    mdiInformation,
} from '@mdi/js';
import Icon from '@mdi/react';
import { Tooltip } from '@mui/material';
import { useEffect, useState } from 'react';
import BlockEditorContainer from '@/components/Block/BlockEditorContainer';

const noteTypes: Array<{
    type: 'info' | 'todo' | 'question' | 'important' | 'section';
    displayName: string;
    icon: string;
    colors: {
        bg: {
            light: string;
            dark: string;
        };
        text: {
            light: string;
            dark: string;
        };
    };
}> = [
    {
        type: 'info',
        displayName: 'Informatie',
        icon: mdiInformation,
        colors: {
            bg: {
                light: 'bg-blue-100',
                dark: 'bg-blue-600',
            },
            text: {
                light: 'text-blue-600',
                dark: 'text-blue-300',
            },
        },
    },
    {
        type: 'section',
        displayName: 'Sectie',
        icon: mdiBookmark,
        colors: {
            bg: {
                light: 'bg-gray-100',
                dark: 'bg-gray-600',
            },
            text: {
                light: 'text-gray-600',
                dark: 'text-gray-300',
            },
        },
    },
    {
        type: 'todo',
        displayName: 'To-do',
        icon: mdiCheckboxMarkedCircle,
        colors: {
            bg: {
                light: 'bg-yellow-100',
                dark: 'bg-yellow-600',
            },
            text: {
                light: 'text-yellow-600',
                dark: 'text-yellow-300',
            },
        },
    },
    {
        type: 'question',
        displayName: 'Vraag',
        icon: mdiHelpCircle,
        colors: {
            bg: {
                light: 'bg-green-100',
                dark: 'bg-green-600',
            },
            text: {
                light: 'text-green-600',
                dark: 'text-green-300',
            },
        },
    },
    {
        type: 'important',
        displayName: 'Belangrijk',
        icon: mdiAlertOctagon,
        colors: {
            bg: {
                light: 'bg-red-100',
                dark: 'bg-red-600',
            },
            text: {
                light: 'text-red-600',
                dark: 'text-red-300',
            },
        },
    },
];

export interface EditorNoteBlockData {
    text: string;
    type: 'info' | 'todo' | 'question' | 'important' | 'section';
}

const EditorNoteBlock = ({ data, uuid, isEditable, onEdited }: BlockProps) => {
    const [blockData, setBlockData] = useState<EditorNoteBlockData>({
        text: data.text?.trim() ?? '',
        type: data.type ?? noteTypes[0].type,
    });

    const noteType =
        noteTypes.find((type) => type.type === blockData.type) ?? noteTypes[0];

    useEffect(() => {
        if (!isEditable) return;
        onEdited?.({
            uuid,
            data: JSON.parse(JSON.stringify(blockData)),
            attachments: {},
        });
    }, [blockData]);

    return (
        <BlockEditorContainer
            className={`relative ps-6 ${noteType.colors.bg.light}`}
        >
            <div>
                <Dropdown
                    className="relative"
                    triggerElement={
                        <Tooltip title="Wissel type" placement="top">
                            <button
                                className={`absolute -top-6 -left-12 rounded-full ${noteType.colors.bg.dark} p-2 text-white shadow-md`}
                            >
                                <Icon path={noteType.icon} size={1.2} />
                            </button>
                        </Tooltip>
                    }
                    alignment={DropdownAlignment.Top}
                >
                    {noteTypes.map((type) => (
                        <DropdownButton
                            key={type.type}
                            icon={type.icon}
                            className={type.colors.text.light}
                            onClick={() =>
                                setBlockData({ ...blockData, type: type.type })
                            }
                        >
                            {type.displayName}
                        </DropdownButton>
                    ))}
                </Dropdown>
                <P
                    className="grow font-bold"
                    placeholder="Redactie notitie"
                    isEditable
                    value={blockData.text}
                    onChange={(value) => {
                        setBlockData({ ...blockData, text: value });
                    }}
                    maxLength={512}
                />
            </div>
        </BlockEditorContainer>
    );
};

export default EditorNoteBlock;
